import type { Seat, ShowState } from './types.js';

/*
 * The guests' messages: one guest to another, the whole table together, and the saboteurs among
 * themselves; and how far each guest has read each of their conversations, for Sent and Read.
 *
 * None of this is show state, on purpose. ShowState goes to every phone, the TV, the table and
 * the cockpit (wire.ts), and a message is for two people, or for the saboteurs of the current
 * draw and nobody else. So a room keeps its messages beside the show, in a book of its own, and
 * hands each phone only what is its own: the cloud room (apps/worker) keeps the book in the
 * Durable Object's storage, so a phone that reconnects gets its history back; the Mac's RUN LOCAL
 * room (apps/desktop/electron/lan.ts) keeps it in memory. Both run these rules, so the two rooms
 * behave the same, and both lose the book with the evening: "Start a new evening" wipes the cloud
 * room, and the local room's is gone when it stops. Nothing here is ever shown to the next crew.
 */

export const CHAT = {
  /** Characters in one message, once it is cleaned. */
  MAX_TEXT: 200,
  /** At most this many messages from one seat in any BURST_MS: a chat, not a flood. */
  BURST: 5,
  BURST_MS: 10_000,
  /** One seat's messages in one evening. */
  MAX_PER_SEAT: 300,
  /** The whole room's, in one evening. */
  MAX_MESSAGES: 3_000,
} as const;

/** The saboteurs' own channel. Only its members are ever sent anything with this name in it. */
export const SABOTEURS = 'saboteurs';

/** The whole table's conversation: everyone checked in reads it (Marek, 2026-10-24). */
export const EVERYONE = 'everyone';

/** A seat, for a private message, the whole table, or the saboteurs' channel. */
export type ChatTo = number | typeof SABOTEURS | typeof EVERYONE;

export interface ChatMessage {
  /** Its place in the evening, from 1. */
  n: number;
  at: number;
  from: number;
  to: ChatTo;
  text: string;
  /**
   * The saboteurs' channel only: the seats that were saboteurs when it was said. Only that same
   * draw ever reads it, so when the saboteurs are drawn again the channel starts afresh, and a new
   * saboteur never learns who the old ones were.
   */
  draw?: number[];
}

/** A message as a phone receives it: the draw it was said in stays in the room. */
export type WireChat = Omit<ChatMessage, 'draw'>;

/**
 * How far one guest has read one of their conversations: its last message `n`, and when (Marek,
 * 2026-10-24: "send and read confirmation").
 */
export interface ChatMark {
  n: number;
  at: number;
  /**
   * The saboteurs' channel only: the draw it was read in. Only that same draw is ever told, so a
   * mark never tells a new saboteur that someone was one before.
   */
  draw?: number[];
}

/** Each seat's marks, by its conversations as that seat sees them: the other's seat, or a channel. */
export type ChatReads = Record<string, Record<string, ChatMark>>;

export interface ChatBook {
  /**
   * A fresh id for every evening, so a phone never takes the new evening's first messages for
   * ones it has already read (the room is always voyage:aurora, and `n` starts again at 1).
   */
  evening: string;
  messages: ChatMessage[];
  reads?: ChatReads;
}

/**
 * Someone else has read a conversation of this phone's up to `n`: the other guest in a private
 * one (`thread` is their seat), or one of the table or of the saboteurs' draw.
 */
export interface ChatSeen {
  seat: number;
  thread: ChatTo;
  n: number;
  at: number;
}

/** Everything one phone may see: its own conversations, and the saboteurs' channel if it is in it. */
export interface ChatView {
  evening: string;
  messages: WireChat[];
  /** The saboteurs of the current draw, this seat among them, while there are two or more; else null. */
  saboteurs: number[] | null;
  /** How far the others have read this phone's conversations. */
  seen?: ChatSeen[];
}

/** What a phone sends: `{ id, chat: { to, text } }`, beside the `{ id, intent }` of everything else. */
export interface ChatSend {
  to: ChatTo;
  text: string;
}

/** What a room sends one phone about its messages, and only that phone. */
export type ChatServerMessage =
  /** On joining, and whenever what it may see has changed: replaces what the phone holds. */
  | { t: 'chatView'; view: ChatView }
  /** One new message for this phone. */
  | { t: 'chat'; message: WireChat }
  /** Someone in one of this phone's conversations has read further. */
  | { t: 'chatSeen'; seen: ChatSeen };

/** What a phone sends when its guest has read a conversation: `{ chatRead: { to, n } }`. */
export interface ChatRead {
  to: ChatTo;
  n: number;
}

/** Why a room turned a message down. The phone says each in plain words (apps/guest/src/chat). */
export const CHAT_REFUSAL = {
  checkIn: 'check in first',
  nobody: 'nobody at that seat',
  self: 'not to yourself',
  channel: 'not in that channel',
  empty: 'empty message',
  long: 'message too long',
  slow: 'slow down',
  full: 'no more messages tonight',
} as const;

export function emptyChat(evening: string): ChatBook {
  return { evening, messages: [] };
}

/**
 * Plain text on one line: no control characters, none of the invisible ones that hide or reorder
 * text, runs of space made one. The joiners inside emoji stay. Nothing is ever turned into a link.
 */
export function cleanChatText(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return Array.from(raw.normalize('NFC'), (ch) => (hidden(ch.codePointAt(0) ?? 0) ? ' ' : ch))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Control characters, zero-width spaces, direction marks and overrides, the word joiner, the BOM. */
function hidden(code: number): boolean {
  return (
    code < 0x20 ||
    (code >= 0x7f && code <= 0x9f) ||
    code === 0x200b ||
    code === 0x200e ||
    code === 0x200f ||
    (code >= 0x202a && code <= 0x202e) ||
    (code >= 0x2060 && code <= 0x2069) ||
    code === 0xfeff
  );
}

/**
 * A frame from a phone, if it is a message. `null` means it is not one (an intent, say), and the
 * room reads it as it always has; `chat: null` means it tried to be one and is malformed.
 */
export function parseChatFrame(raw: string): { id?: number; chat: ChatSend | null } | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || !('chat' in parsed)) return null;
  const { id, chat } = parsed as { id?: unknown; chat?: unknown };
  const withId = typeof id === 'number' ? { id } : {};
  if (typeof chat !== 'object' || chat === null) return { ...withId, chat: null };
  const { to, text } = chat as { to?: unknown; text?: unknown };
  const target = chatTarget(to);
  if (target === null || typeof text !== 'string') return { ...withId, chat: null };
  return { ...withId, chat: { to: target, text } };
}

function chatTarget(to: unknown): ChatTo | null {
  if (to === SABOTEURS || to === EVERYONE) return to;
  return typeof to === 'number' && Number.isInteger(to) ? to : null;
}

/** A frame from a phone saying how far its guest has read, if it is one; malformed ones are dropped. */
export function parseChatRead(raw: string): ChatRead | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || !('chatRead' in parsed)) return null;
  const read = (parsed as { chatRead?: unknown }).chatRead;
  if (typeof read !== 'object' || read === null) return null;
  const { to, n } = read as { to?: unknown; n?: unknown };
  const target = chatTarget(to);
  if (target === null || typeof n !== 'number' || !Number.isInteger(n) || n < 1) return null;
  return { to: target, n };
}

/** At the table and checked in: someone a message can come from, or go to. */
function aboard(seat: Seat | undefined): seat is Seat {
  return Boolean(seat && seat.callsign !== null && seat.profession !== null);
}

/** The saboteurs of the current draw, by seat, in order. */
export function saboteursOf(state: Pick<ShowState, 'seats'>): number[] {
  return state.seats
    .filter((s) => s.alignment === 'PARADOX AGENT')
    .map((s) => s.seat)
    .sort((a, b) => a - b);
}

function sameSeats(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((seat, i) => seat === b[i]);
}

/** The channel this seat may use: the whole draw, when it is in it and not alone. */
export function saboteurChannel(state: Pick<ShowState, 'seats'>, seat: number): number[] | null {
  const draw = saboteursOf(state);
  return draw.length >= 2 && draw.includes(seat) ? draw : null;
}

export function toWireChat(message: ChatMessage): WireChat {
  const { draw: _draw, ...rest } = message;
  return rest;
}

/**
 * Who a message is for, sender included: the two of them, the draw it was said in, or for the
 * whole table every seat (each phone at the table; the room hands it to guests only).
 */
export function recipientsOf(message: ChatMessage, state?: Pick<ShowState, 'seats'>): number[] {
  if (message.to === SABOTEURS) return [...(message.draw ?? [])];
  if (message.to === EVERYONE) return state ? state.seats.map((s) => s.seat) : [message.from];
  return message.from === message.to ? [message.from] : [message.from, message.to];
}

/** Whether this seat may read this message, with the saboteurs as they are now. */
function readable(message: ChatMessage, seat: number, draw: readonly number[]): boolean {
  if (message.to === EVERYONE) return true;
  if (message.to === SABOTEURS) return draw.includes(seat) && sameSeats(message.draw ?? [], draw);
  return message.from === seat || message.to === seat;
}

export function chatViewFor(book: ChatBook, state: Pick<ShowState, 'seats'>, seat: number): ChatView {
  const draw = saboteursOf(state);
  const channel = saboteurChannel(state, seat);
  // The others' marks in this phone's conversations: theirs with it, the table's, and the draw's while
  // this phone is in it — a saboteurs' mark only from this same draw.
  const seen: ChatSeen[] = [];
  for (const [who, marks] of Object.entries(book.reads ?? {})) {
    const other = Number(who);
    if (other === seat) continue;
    const mine = marks[String(seat)];
    if (mine) seen.push({ seat: other, thread: other, n: mine.n, at: mine.at });
    const all = marks[EVERYONE];
    if (all) seen.push({ seat: other, thread: EVERYONE, n: all.n, at: all.at });
    const secret = marks[SABOTEURS];
    if (secret && channel && channel.includes(other) && sameSeats(secret.draw ?? [], channel)) {
      seen.push({ seat: other, thread: SABOTEURS, n: secret.n, at: secret.at });
    }
  }
  return {
    evening: book.evening,
    messages: book.messages.filter((m) => readable(m, seat, draw)).map(toWireChat),
    saboteurs: channel,
    seen,
  };
}

export type ChatResult =
  | { ok: true; book: ChatBook; message: ChatMessage; to: number[] }
  | { ok: false; error: string };

/**
 * A message from `from`, who the room has already made sure holds that seat. Checks it, cleans it,
 * and adds it to the book; the room then hands it to `to` and nobody else.
 */
export function sendChat(
  book: ChatBook,
  state: Pick<ShowState, 'seats'>,
  from: number,
  send: ChatSend,
  now: number,
): ChatResult {
  const refuse = (error: string): ChatResult => ({ ok: false, error });
  if (!aboard(state.seats.find((s) => s.seat === from))) return refuse(CHAT_REFUSAL.checkIn);

  let draw: number[] | undefined;
  if (send.to === SABOTEURS) {
    const channel = saboteurChannel(state, from);
    if (!channel) return refuse(CHAT_REFUSAL.channel);
    draw = channel;
  } else if (send.to !== EVERYONE) {
    if (send.to === from) return refuse(CHAT_REFUSAL.self);
    if (!aboard(state.seats.find((s) => s.seat === send.to))) return refuse(CHAT_REFUSAL.nobody);
  }

  const text = cleanChatText(send.text);
  if (!text) return refuse(CHAT_REFUSAL.empty);
  if ([...text].length > CHAT.MAX_TEXT) return refuse(CHAT_REFUSAL.long);

  // The limits are read off the book itself, so a room that wakes from sleep still knows them.
  const mine = book.messages.filter((m) => m.from === from);
  const recent = mine.filter((m) => now - m.at < CHAT.BURST_MS).length;
  if (recent >= CHAT.BURST) return refuse(CHAT_REFUSAL.slow);
  if (mine.length >= CHAT.MAX_PER_SEAT || book.messages.length >= CHAT.MAX_MESSAGES) {
    return refuse(CHAT_REFUSAL.full);
  }

  const last = book.messages[book.messages.length - 1]?.n ?? 0;
  const message: ChatMessage = { n: last + 1, at: now, from, to: send.to, text, ...(draw ? { draw } : {}) };
  return { ok: true, book: { ...book, messages: [...book.messages, message] }, message, to: recipientsOf(message, state) };
}

export type ReadResult =
  | { ok: true; book: ChatBook; tell: { seat: number; seen: ChatSeen }[] }
  | { ok: false };

/**
 * `seat`'s guest has read conversation `to` up to `n`, as their phone says. Kept only if it is
 * further than before and no further than the conversation goes; the room then tells `tell`, the
 * others in that conversation, and nobody else: the other guest of a private one, the rest of the
 * table, or the rest of the saboteurs' draw while this seat is in it.
 */
export function readChat(book: ChatBook, state: Pick<ShowState, 'seats'>, seat: number, read: ChatRead, now: number): ReadResult {
  const draw = saboteursOf(state);
  let others: number[];
  let channel: number[] | undefined;
  if (read.to === SABOTEURS) {
    const own = saboteurChannel(state, seat);
    if (!own) return { ok: false };
    channel = own;
    others = own.filter((s) => s !== seat);
  } else if (read.to === EVERYONE) {
    others = state.seats.map((s) => s.seat).filter((s) => s !== seat);
  } else {
    if (read.to === seat) return { ok: false };
    others = [read.to];
  }
  const key = String(read.to);
  const last = book.messages.filter((m) => readable(m, seat, draw) && threadKeyOf(m, seat) === key).at(-1)?.n ?? 0;
  const n = Math.min(read.n, last);
  const marks = book.reads?.[String(seat)] ?? {};
  // A saboteurs' mark from another draw counts for nothing: the channel started afresh.
  const was = marks[key];
  const counts = was && (!channel || sameSeats(was.draw ?? [], channel));
  if (n < 1 || (counts && was.n >= n)) return { ok: false };
  const mark: ChatMark = { n, at: now, ...(channel ? { draw: channel } : {}) };
  const reads: ChatReads = { ...(book.reads ?? {}), [String(seat)]: { ...marks, [key]: mark } };
  // The other guest of a private conversation knows it by this seat; a channel by its name.
  const thread: ChatTo = typeof read.to === 'number' ? seat : read.to;
  return { ok: true, book: { ...book, reads }, tell: others.map((s) => ({ seat: s, seen: { seat, thread, n, at: now } })) };
}

/** The conversation a message is in, as `seat` sees it: the other guest's seat, or the channel. */
function threadKeyOf(message: ChatMessage, seat: number): string {
  if (typeof message.to !== 'number') return message.to;
  return String(message.from === seat ? message.to : message.from);
}

/**
 * After the steward swaps two guests or checks one out. Messages follow the people: a swap moves
 * a guest's conversations with them, as it moves their points; a guest who is checked out takes
 * theirs away, so whoever sits there next never reads them. A saboteurs' message goes with them
 * too if one of that draw has left.
 */
export function moveChat(
  book: ChatBook,
  moved: readonly { from: number; to: number }[],
  freed: readonly number[],
): ChatBook {
  if (moved.length === 0 && freed.length === 0) return book;
  // A swap: each named move, and its way back for a seat that was empty and so was not named.
  const to = new Map<number, number>();
  for (const m of moved) to.set(m.from, m.to);
  for (const m of moved) if (!to.has(m.to)) to.set(m.to, m.from);
  const place = (seat: number) => to.get(seat) ?? seat;
  const gone = (seat: number) => freed.includes(seat);

  const messages: ChatMessage[] = [];
  for (const m of book.messages) {
    if (gone(m.from) || (typeof m.to === 'number' && gone(m.to)) || (m.draw ?? []).some(gone)) continue;
    messages.push({
      ...m,
      from: place(m.from),
      to: typeof m.to === 'number' ? place(m.to) : m.to,
      ...(m.draw ? { draw: m.draw.map(place).sort((a, b) => a - b) } : {}),
    });
  }
  // How far each has read goes with them too; a guest who leaves takes theirs, and nobody keeps a
  // mark of a conversation with them.
  let reads: ChatReads | undefined;
  if (book.reads) {
    reads = {};
    for (const [who, marks] of Object.entries(book.reads)) {
      const seat = Number(who);
      if (gone(seat)) continue;
      const next: Record<string, ChatMark> = {};
      for (const [key, mark] of Object.entries(marks)) {
        if (key === SABOTEURS) {
          if ((mark.draw ?? []).some(gone)) continue;
          next[key] = mark.draw ? { ...mark, draw: mark.draw.map(place).sort((a, b) => a - b) } : mark;
        } else if (key === EVERYONE) {
          next[key] = mark;
        } else if (!gone(Number(key))) {
          next[String(place(Number(key)))] = mark;
        }
      }
      reads[String(place(seat))] = next;
    }
  }
  return { ...book, messages, ...(reads ? { reads } : {}) };
}

/**
 * The seats whose view has changed because the saboteurs have: a draw at launch, a redraw, the
 * steward setting a role. Everyone who was in the old draw or is in the new one is sent their
 * view again; nobody else hears a thing, so nothing tells the crew that anything moved.
 */
export function chatRedrawn(before: Pick<ShowState, 'seats'>, after: Pick<ShowState, 'seats'>): number[] {
  const was = saboteursOf(before);
  const now = saboteursOf(after);
  if (sameSeats(was, now)) return [];
  return [...new Set([...was, ...now])].sort((a, b) => a - b);
}
